import { useId } from 'react';
import { cn } from './lib/cn';

interface LogoProps {
  size?: number;
  primary?: string;
  stop1?: string;
  stop2?: string;
  showWordmark?: boolean;
  wordmarkColor?: string;
  className?: string;
  title?: string;
}

export function Logo({
  size = 32,
  primary = '#4F46E5',
  stop1 = '#3730A3',
  stop2 = '#818CF8',
  showWordmark = false,
  wordmarkColor = '#0A0A0A',
  className,
  title = 'Clarix',
}: LogoProps) {
  const reactId = useId();
  const gradId = `clarix-grad-${reactId.replace(/:/g, '')}`;

  const mark = (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden={showWordmark ? true : undefined}
      role={showWordmark ? undefined : 'img'}
      aria-label={showWordmark ? undefined : title}
    >
      <rect x="20" y="20" width="40" height="40" rx="12" fill={primary} />
      <rect x="60" y="60" width="40" height="40" rx="12" fill={stop2} />
      <path
        d="M70 20C70 20 100 20 100 50"
        stroke={`url(#${gradId})`}
        strokeWidth="16"
        strokeLinecap="round"
      />
      <path
        d="M50 100C50 100 20 100 20 70"
        stroke={`url(#${gradId})`}
        strokeWidth="16"
        strokeLinecap="round"
      />
      <defs>
        <linearGradient
          id={gradId}
          x1="20"
          y1="20"
          x2="100"
          y2="100"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={stop1} />
          <stop offset="0.5" stopColor={primary} />
          <stop offset="1" stopColor={stop2} />
        </linearGradient>
      </defs>
    </svg>
  );

  if (!showWordmark) {
    return <span className={className}>{mark}</span>;
  }

  const textSize = Math.round(size * 0.85);
  const gap = Math.max(6, Math.round(size * 0.25));

  return (
    <span
      className={cn('inline-flex items-center', className)}
      style={{ gap: `${gap}px` }}
      role="img"
      aria-label={title}
    >
      {mark}
      <span
        className="font-bold leading-none tracking-[-0.03em]"
        style={{ fontSize: `${textSize}px`, color: wordmarkColor }}
      >
        Clarix
      </span>
    </span>
  );
}
